namespace Client.Forms

open System
open Client.Components
open Client.Components.Select
open Client.Infrastructure.Api
open Client.InfrastructureTypes
open Client.DomainTypes.Msg
open Elmish
open Fable.React
open Fable.React.Props
open Fulma
open Microsoft.FSharp.Core
open Shared.Dto
open Shared.DtoTypes
open Shared.Infrastructure
open Shared.Dto.Dto
open Shared
open Client
open Thoth.Elmish


[<RequireQualifiedAccess>]
module AlertForm =
    type Model = {
        Session: UserSession
        AvailableSensors: IdValue<MapSensorDto> list
        AvailableContactInfos: AlertDto.ContactInfo list
        Sensor: IdValue<MapSensorDto> option
        ContactInfo: AlertDto.ContactInfo option
        TemperatureThresholdType: AlertDto.TemperatureType
        PhoneAlertType: AlertDto.PhoneAlertType
        Threshold: string option
        ActiveUntil: DatePicker
        RequestRunning: bool
    }

    type FormResult =
        | Noop
        | CloseModal
        | CloseAndRefresh

    let private thresholdTypeFromString typeString =
        match typeString with
        | "Feuchttemperatur" -> AlertDto.Wet
        | "Trockentemperatur" -> AlertDto.Dry
        | _ -> failwith "Unbekannter ThresholdTyp"

    let private phoneAlertTypeFromString alertType =
        match alertType with
        | "Anruf" -> AlertDto.Call
        | "SMS" -> AlertDto.SMS
        | _ -> failwith "That shouldn't happen"

    let init session sensors contactInfos =
        let endDate = DateTime.addMonths 6 DateTime.Now

        let selectedDate = DateTime.addMonths 1 DateTime.Now

        {
            Session = session
            AvailableSensors = sensors
            AvailableContactInfos = contactInfos
            Sensor = List.tryHead sensors
            ContactInfo = List.tryHead contactInfos
            TemperatureThresholdType = AlertDto.Dry
            PhoneAlertType = AlertDto.SMS
            Threshold = Some "2.0"
            ActiveUntil = DatePicker.init DateTime.Now (Some endDate) selectedDate
            RequestRunning = false
        }

    let private createAlert
        userId
        (sensor: IdValue<MapSensorDto>)
        (contactInfo: AlertDto.ContactInfo)
        (thresholdType: AlertDto.TemperatureType)
        (phoneAlertType: AlertDto.PhoneAlertType)
        (threshold: float)
        (activeUntil: DateTime)
        : AlertDto.NewAlert =

        {
            SensorId = sensor.Id
            ContactInfoId = contactInfo.Id
            TemperatureType = thresholdType
            UserId = userId
            PhoneAlertType = phoneAlertType
            Threshold = threshold
            ActiveUntil = activeUntil
        }

    let private checkThresholdLimits (threshold: float) =
        if threshold >= -10.0 && threshold <= 4.0 then
            Some threshold
        else
            None

    let createAlertMsg (model: Model) =
        let threshold =
            model.Threshold
            |> Option.bind Inputs.toFloatOption
            |> Option.bind checkThresholdLimits

        Some createAlert
        |> Option.apply (Some model.Session.User.Id)
        |> Option.apply model.Sensor
        |> Option.apply model.ContactInfo
        |> Option.apply (Some model.TemperatureThresholdType)
        |> Option.apply (Some model.PhoneAlertType)
        |> Option.apply threshold
        |> Option.apply (Some model.ActiveUntil.SelectedDate)
        |> Option.map AlertFormMsg.CreateAlert

    let typeToOption (thresholdType: AlertDto.TemperatureType) =
        let string = AlertDto.thresholdTypeToString thresholdType

        option [ Value string ] [ str string ]

    let private thresholdTypeSelect (selected: AlertDto.TemperatureType) dispatch =
        let types = [ AlertDto.Wet; AlertDto.Dry ]
        let options = List.map typeToOption types

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue(AlertDto.thresholdTypeToString selected)
                    OnChange(fun event -> dispatch (event.Value |> thresholdTypeFromString |> ThresholdTypeChanged))
                ]
                options
        ]

    let private activeUntilInput dispatch state =
        DatePicker.view "Datum" (AlertFormMsg.ActiveUntilChanged >> dispatch) state

    let private contactInfoToOption (info: AlertDto.ContactInfo) =
        let string = info.Name

        option [ Value string ] [ str string ]

    let private contactInfoSelect
        (selected: AlertDto.ContactInfo option)
        (availableContactInfos: AlertDto.ContactInfo list)
        dispatch
        =
        let options = List.map contactInfoToOption availableContactInfos

        let findContactInfo name =
            availableContactInfos
            |> List.tryFind (fun contactInfo -> contactInfo.Name = name)

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue(
                        selected
                        |> Option.map (fun contactInfo -> contactInfo.Name)
                        |> Option.defaultValue ""
                    )
                    OnChange(fun event -> dispatch (event.Value |> findContactInfo |> ContactInfoChanged))
                ]
                options
        ]

    let alertTypeToOption (alertType: AlertDto.PhoneAlertType) =
        let string = AlertDto.phoneAlertTypeToString alertType

        option [ Value string ] [ str string ]

    let private alertTypeSelect (selected: AlertDto.PhoneAlertType) dispatch =
        let types = [ AlertDto.Call; AlertDto.SMS ]
        let options = List.map alertTypeToOption types

        Select.select [ Select.IsFullWidth ] [
            select
                [
                    DefaultValue(AlertDto.phoneAlertTypeToString selected)
                    OnChange(fun event -> dispatch (event.Value |> phoneAlertTypeFromString |> PhoneAlertTypeChanged))
                ]
                options
        ]

    let form dispatch (model: Model) =
        form [] [
            Field.div [] [
                Label.label [] [ str "Sensor" ]
                Control.div [ Control.IsExpanded ] [
                    MapSensorSelect.view (SensorChanged >> dispatch) model.Sensor model.AvailableSensors false
                ]
            ]
            Field.div [] [
                Label.label [] [ str "Kontaktdaten" ]
                Control.div [ Control.IsExpanded ] [
                    contactInfoSelect model.ContactInfo model.AvailableContactInfos dispatch
                ]
            ]
            Field.div [] [
                Label.label [] [ str "Bewarnungsart" ]
                Control.div [ Control.IsExpanded ] [ alertTypeSelect model.PhoneAlertType dispatch ]
            ]
            Field.div [] [
                Label.label [] [ str "Verwendete Temperatur" ]
                Control.div [ Control.IsExpanded ] [
                    thresholdTypeSelect model.TemperatureThresholdType dispatch
                ]
            ]
            Field.div [] [
                Label.label [] [ str "Temperatur Grenzwert" ]
                Control.div [] [
                    Input.text [
                        model.Threshold |> Option.defaultValue "" |> Input.Value
                        Input.OnChange(fun event ->
                            event.Value |> String.toOption |> AlertFormMsg.ThresholdChanged |> dispatch
                        )
                    ]
                    Help.help [] [
                        str "Der Grenzwert muss zwischen +4˚C und -10˚C liegen"
                    ]
                ]
            ]
            Field.div [] [
                Label.label [] [ str "Aktiv bis" ]
                Control.div [ Control.IsExpanded ] [
                    activeUntilInput dispatch model.ActiveUntil

                    Help.help [] [
                        str "An diesem Datum wird diese Bewarnung automatisch gestoppt"
                    ]
                ]
            ]
        ]

    let saveButton dispatch (model: Model) =
        let maybeOnClick =
            createAlertMsg model
            |> Option.map (fun msg -> Button.OnClick(fun _ -> dispatch msg))

        let buttonOptions = [
            Button.IsLoading model.RequestRunning
            Button.Color IsSuccess
            Button.Disabled(Option.isNone maybeOnClick)
        ]

        Button.button (List.addToListIfSome buttonOptions maybeOnClick) [ str "Erstellen" ]


    let view (dispatch: AlertFormMsg -> unit) (model: Model) =
        let closeModal = (fun _ -> dispatch AlertFormMsg.CloseModal)

        let headline = "Neue Bewarnung anlegen"

        Modal.modal [ Modal.IsActive true ] [
            Modal.background [] []
            Modal.Card.card [] [
                Modal.Card.head [] [
                    Modal.Card.title [] [ str headline ]
                    Delete.delete [ Delete.OnClick closeModal ] []
                ]
                Modal.Card.body [] [ Content.content [] [ form dispatch model ] ]
                Modal.Card.foot [] [ saveButton dispatch model ]
            ]
        ]

    let update (msg: AlertFormMsg) (model: Model) =
        match msg with
        | SensorChanged sensor -> { model with Sensor = sensor }, Cmd.none, FormResult.Noop
        | ThresholdTypeChanged thresholdType ->
            { model with TemperatureThresholdType = thresholdType }, Cmd.none, FormResult.Noop
        | PhoneAlertTypeChanged alertType -> { model with PhoneAlertType = alertType }, Cmd.none, FormResult.Noop
        | ContactInfoChanged contactInfo -> { model with ContactInfo = contactInfo }, Cmd.none, FormResult.Noop
        | ThresholdChanged threshold -> { model with Threshold = threshold }, Cmd.none, FormResult.Noop
        | CreateAlert alert ->
            let request = {
                SessionKey = model.Session.SessionKey
                Data = alert
            }

            { model with RequestRunning = true },
            Cmd.OfAsync.perform api.createAlert request AlertCreated,
            FormResult.Noop
        | AlertCreated response ->
            match response with
            | Ok _ ->
                let toastCmd = Toast.create "Speichern erfolgreich" |> Toast.success

                model, toastCmd, FormResult.CloseAndRefresh
            | Error _ ->
                let toastCmd = Toast.create "Fehler beim Speichern" |> Toast.error

                model, toastCmd, FormResult.Noop
        | AlertFormMsg.CloseModal -> model, Cmd.none, FormResult.CloseModal
        | AlertFormMsg.ActiveUntilChanged activeUntil ->
            let newActiveUntil = DatePicker.updateSelectedDate model.ActiveUntil activeUntil

            { model with ActiveUntil = newActiveUntil }, Cmd.none, FormResult.Noop